import React, { useEffect, useState } from "react";
import { adminApi } from "@/lib/adminApi";
import { Field } from "@/pages/admin/Field";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowUp, ArrowDown, Plus, Trash2, Eye, History, KeyRound } from "lucide-react";
import DOMPurify from "dompurify";

/* ------------------------- SITE INFO ------------------------- */
export const SiteInfoSection = () => {
  const [f, setF] = useState(null);
  useEffect(() => { adminApi.getSiteInfo().then(setF); }, []);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const tog = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => { try { await adminApi.putSiteInfo(f); toast.success("Saved — reload the site"); } catch { toast.error("Save failed"); } };
  if (!f) return <p className="text-sm text-gmoaa-text-muted">Loading…</p>;
  return (
    <div className="max-w-2xl space-y-4">
      <h2 className="font-serif text-2xl font-semibold text-gmoaa-green">Site settings</h2>
      <div className="space-y-4 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Short name (English)" value={f.assoc_short_en} onChange={set("assoc_short_en")} testid="si-short-en" />
          <Field label="Short name (Gujarati)" value={f.assoc_short_gu} onChange={set("assoc_short_gu")} testid="si-short-gu" />
        </div>
        <Field label="Full name (English)" value={f.assoc_name_en} onChange={set("assoc_name_en")} testid="si-name-en" />
        <Field label="Full name (Gujarati)" value={f.assoc_name_gu} onChange={set("assoc_name_gu")} testid="si-name-gu" />
        <Field label="Address (English)" textarea value={f.address_en} onChange={set("address_en")} testid="si-addr-en" />
        <Field label="Address (Gujarati)" textarea value={f.address_gu} onChange={set("address_gu")} testid="si-addr-gu" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <Field label="Email" value={f.email} onChange={set("email")} testid="si-email" />
          <Field label="Phone" value={f.phone} onChange={set("phone")} testid="si-phone" />
          <Field label="Website" value={f.website} onChange={set("website")} testid="si-website" />
        </div>
        <div className="flex flex-wrap gap-5">
          {[["show_address", "Show address"], ["show_email", "Show email"], ["show_phone", "Show phone"]].map(([k, l]) => (
            <label key={k} className="flex items-center gap-2 text-sm"><Switch checked={f[k] !== false} onCheckedChange={tog(k)} data-testid={`si-${k}`} /> {l}</label>
          ))}
        </div>
        <Button onClick={save} data-testid="si-save" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save site settings</Button>
      </div>
    </div>
  );
};

/* ------------------------- FEATURE SWITCHES ------------------------- */
export const FeaturesSection = () => {
  const [f, setF] = useState(null);
  useEffect(() => { adminApi.getFeatures().then(setF); }, []);
  const tog = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => { try { await adminApi.putFeatures(f); toast.success("Feature switches saved"); } catch { toast.error("Save failed"); } };
  if (!f) return <p className="text-sm text-gmoaa-text-muted">Loading…</p>;
  const items = [
    ["razorpay", "Razorpay online payments", "Allow members to pay dues online by card/UPI"],
    ["upi_qr", "UPI QR mode", "Show a UPI QR code option for payments"],
    ["counters", "Public visitor counters", "Show the live visitors / members-online strip on the home page"],
    ["gapl", "GAPL sections", "Show GAPL countdown, hub and match-centre content"],
    ["sample_labels", "Sample-data labels", "Show the small 'Sample data' badges on seeded content"],
  ];
  return (
    <div className="max-w-2xl space-y-4">
      <h2 className="font-serif text-2xl font-semibold text-gmoaa-green">Feature switches</h2>
      <div className="space-y-3 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
        {items.map(([k, title, desc]) => (
          <div key={k} className="flex items-center justify-between border-b border-gmoaa-border-subtle py-2 last:border-0">
            <div><p className="text-sm font-medium text-gmoaa-text-dark">{title}</p><p className="text-xs text-gmoaa-text-muted">{desc}</p></div>
            <Switch checked={!!f[k]} onCheckedChange={tog(k)} data-testid={`feat-${k}`} />
          </div>
        ))}
        <Button onClick={save} data-testid="feat-save" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save switches</Button>
      </div>
    </div>
  );
};

/* ------------------------- NAVIGATION ------------------------- */
const LinkRows = ({ rows, setRows, prefix }) => {
  const move = (i, d) => { const a = [...rows]; const j = i + d; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; setRows(a); };
  const upd = (i, k, v) => { const a = [...rows]; a[i] = { ...a[i], [k]: v }; setRows(a); };
  const add = () => setRows([...rows, { label_en: "", label_gu: "", to: "/", show: true }]);
  const rm = (i) => setRows(rows.filter((_, x) => x !== i));
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-12 items-center gap-2 rounded border border-gmoaa-border-subtle p-2" data-testid={`${prefix}-row-${i}`}>
          <Input className="col-span-3 h-8 text-sm" value={r.label_en} onChange={(e) => upd(i, "label_en", e.target.value)} placeholder="English" data-testid={`${prefix}-en-${i}`} />
          <Input className="col-span-3 h-8 text-sm" value={r.label_gu} onChange={(e) => upd(i, "label_gu", e.target.value)} placeholder="ગુજરાતી" />
          <Input className="col-span-3 h-8 text-sm" value={r.to} onChange={(e) => upd(i, "to", e.target.value)} placeholder="/path" data-testid={`${prefix}-to-${i}`} />
          <div className="col-span-3 flex items-center justify-end gap-1">
            <Switch checked={r.show !== false} onCheckedChange={(v) => upd(i, "show", v)} data-testid={`${prefix}-show-${i}`} />
            <button onClick={() => move(i, -1)} className="p-1 text-gmoaa-text-muted hover:text-gmoaa-green"><ArrowUp size={14} /></button>
            <button onClick={() => move(i, 1)} className="p-1 text-gmoaa-text-muted hover:text-gmoaa-green"><ArrowDown size={14} /></button>
            <button onClick={() => rm(i)} className="p-1 text-gmoaa-red"><Trash2 size={14} /></button>
          </div>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={add} data-testid={`${prefix}-add`}><Plus size={14} className="mr-1" /> Add link</Button>
    </div>
  );
};

export const NavigationSection = () => {
  const [main, setMain] = useState([]);
  const [quick, setQuick] = useState([]);
  const [legal, setLegal] = useState([]);
  useEffect(() => { adminApi.getNav().then((d) => { setMain(d.main || []); setQuick(d.footer?.quick || []); setLegal(d.footer?.legal || []); }); }, []);
  const save = async () => { try { await adminApi.putNav({ main, footer: { quick, legal } }); toast.success("Navigation saved — reload the site"); } catch { toast.error("Save failed"); } };
  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl font-semibold text-gmoaa-green">Navigation</h2>
        <Button onClick={save} data-testid="nav-save" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save navigation</Button>
      </div>
      <div><p className="mb-2 text-sm font-semibold text-gmoaa-text-dark">Main menu</p><LinkRows rows={main} setRows={setMain} prefix="navmain" /></div>
      <div><p className="mb-2 text-sm font-semibold text-gmoaa-text-dark">Footer — Quick links</p><LinkRows rows={quick} setRows={setQuick} prefix="navquick" /></div>
      <div><p className="mb-2 text-sm font-semibold text-gmoaa-text-dark">Footer — Legal links</p><LinkRows rows={legal} setRows={setLegal} prefix="navlegal" /></div>
    </div>
  );
};

/* ------------------------- HOME MANAGER ------------------------- */
export const HomeManagerSection = () => {
  const [h, setH] = useState(null);
  useEffect(() => { adminApi.getHomeConfig().then(setH); }, []);
  const save = async () => { try { await adminApi.putHomeConfig(h); toast.success("Home saved — reload the site"); } catch { toast.error("Save failed"); } };
  if (!h) return <p className="text-sm text-gmoaa-text-muted">Loading…</p>;

  const setHero = (k, v) => setH((x) => ({ ...x, hero: { ...x.hero, [k]: v } }));
  const setGapl = (k, v) => setH((x) => ({ ...x, gapl: { ...x.gapl, [k]: v } }));
  const moveSec = (i, d) => { const a = [...h.sections]; const j = i + d; if (j < 0 || j >= a.length) return; [a[i], a[j]] = [a[j], a[i]]; a.forEach((s, idx) => s.order = idx); setH({ ...h, sections: a }); };
  const togSec = (i, v) => { const a = [...h.sections]; a[i] = { ...a[i], show: v }; setH({ ...h, sections: a }); };
  const setStat = (i, k, v) => { const a = [...h.stats]; a[i] = { ...a[i], [k]: v }; setH({ ...h, stats: a }); };

  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl font-semibold text-gmoaa-green">Home page</h2>
        <Button onClick={save} data-testid="home-save" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save home</Button>
      </div>

      <div className="rounded-xl border border-gmoaa-border-subtle bg-white p-5">
        <p className="mb-3 text-sm font-semibold text-gmoaa-text-dark">Sections — order &amp; visibility</p>
        {h.sections.map((s, i) => (
          <div key={s.key} className="flex items-center justify-between border-b border-gmoaa-border-subtle py-2 last:border-0" data-testid={`home-sec-${s.key}`}>
            <span className="text-sm">{s.label || s.key}</span>
            <div className="flex items-center gap-1">
              <Switch checked={s.show !== false} onCheckedChange={(v) => togSec(i, v)} data-testid={`home-sec-show-${s.key}`} />
              <button onClick={() => moveSec(i, -1)} className="p-1 text-gmoaa-text-muted hover:text-gmoaa-green"><ArrowUp size={14} /></button>
              <button onClick={() => moveSec(i, 1)} className="p-1 text-gmoaa-text-muted hover:text-gmoaa-green"><ArrowDown size={14} /></button>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-3 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
        <p className="text-sm font-semibold text-gmoaa-text-dark">Hero</p>
        <Field label="Badge (English)" value={h.hero.badge_en} onChange={(v) => setHero("badge_en", v)} testid="home-badge-en" />
        <Field label="Badge (Gujarati)" value={h.hero.badge_gu} onChange={(v) => setHero("badge_gu", v)} />
        <label className="flex items-center gap-2 text-sm"><Switch checked={h.hero.show_badge !== false} onCheckedChange={(v) => setHero("show_badge", v)} data-testid="home-badge-show" /> Show badge</label>
        <Field label="Headline (English)" value={h.hero.title_en} onChange={(v) => setHero("title_en", v)} testid="home-title-en" />
        <Field label="Headline (Gujarati)" value={h.hero.title_gu} onChange={(v) => setHero("title_gu", v)} />
        <Field label="Subtext (English)" textarea value={h.hero.subtitle_en} onChange={(v) => setHero("subtitle_en", v)} />
        <Field label="Subtext (Gujarati)" textarea value={h.hero.subtitle_gu} onChange={(v) => setHero("subtitle_gu", v)} />
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Primary button (English)" value={h.hero.primary_en} onChange={(v) => setHero("primary_en", v)} />
          <Field label="Secondary button (English)" value={h.hero.secondary_en} onChange={(v) => setHero("secondary_en", v)} />
        </div>
      </div>

      <div className="space-y-3 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
        <p className="text-sm font-semibold text-gmoaa-text-dark">Stat cards</p>
        {h.stats.map((s, i) => (
          <div key={s.key} className="grid grid-cols-12 items-center gap-2 border-b border-gmoaa-border-subtle py-2 last:border-0" data-testid={`home-stat-${s.key}`}>
            <Input className="col-span-3 h-8 text-sm" value={s.label_en} onChange={(e) => setStat(i, "label_en", e.target.value)} placeholder="Label EN" />
            <Input className="col-span-3 h-8 text-sm" value={s.label_gu} onChange={(e) => setStat(i, "label_gu", e.target.value)} placeholder="Label GU" />
            <select className="col-span-3 h-8 rounded border border-gmoaa-border-subtle text-sm" value={s.mode} onChange={(e) => setStat(i, "mode", e.target.value)} data-testid={`home-stat-mode-${s.key}`}>
              <option value="manual">Manual value</option>
              <option value="auto">Automatic</option>
            </select>
            {s.mode === "auto"
              ? <span className="col-span-2 text-xs text-gmoaa-text-muted">from {s.source}</span>
              : <Input className="col-span-2 h-8 text-sm" value={s.value} onChange={(e) => setStat(i, "value", e.target.value)} placeholder="value" data-testid={`home-stat-val-${s.key}`} />}
            <div className="col-span-1 flex justify-end"><Switch checked={s.show !== false} onCheckedChange={(v) => setStat(i, "show", v)} data-testid={`home-stat-show-${s.key}`} /></div>
          </div>
        ))}
      </div>

      <div className="space-y-3 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
        <p className="text-sm font-semibold text-gmoaa-text-dark">GAPL countdown</p>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <Field label="Season number" value={h.gapl.season} onChange={(v) => setGapl("season", v)} testid="home-gapl-season" />
          <Field label="Start date &amp; time (ISO, e.g. 2026-12-20T10:00:00+05:30)" value={h.gapl.start_date} onChange={(v) => setGapl("start_date", v)} testid="home-gapl-start" />
        </div>
        <Field label="Season name (English)" value={h.gapl.season_name_en} onChange={(v) => setGapl("season_name_en", v)} />
        <Field label="Season name (Gujarati)" value={h.gapl.season_name_gu} onChange={(v) => setGapl("season_name_gu", v)} />
        <Field label="Venue (English)" value={h.gapl.venue_en} onChange={(v) => setGapl("venue_en", v)} />
        <Field label="Countdown title (English)" value={h.gapl.countdown_title_en} onChange={(v) => setGapl("countdown_title_en", v)} />
        <Field label="GAPL hero image URL (upload in Media, paste URL)" value={h.gapl.hero_image_url} onChange={(v) => setGapl("hero_image_url", v)} testid="home-gapl-img" />
        <label className="flex items-center gap-2 text-sm"><Switch checked={h.gapl.show_countdown !== false} onCheckedChange={(v) => setGapl("show_countdown", v)} data-testid="home-gapl-show" /> Show countdown on home</label>
      </div>
    </div>
  );
};

/* ------------------------- TEMPLATES ------------------------- */
const TPL_DEFS = [
  { key: "email_otp", title: "Email — login OTP", vars: { code: "428913" }, fields: ["subject", "heading", "body"] },
  { key: "email_receipt", title: "Email — payment receipt", vars: { name: "Dr. A. Patel", number: "GMOAA/2026/001", purpose: "Membership fee", amount: "500" }, fields: ["subject", "heading", "body"] },
  { key: "email_demand", title: "Email — fee notice", vars: { name: "Dr. A. Patel", fee: "Annual Membership", fy: "2026-27", amount: "500", due: "31/12/2026" }, fields: ["subject", "heading", "body"] },
  { key: "email_renewal", title: "Email — registration renewal", vars: { name: "Dr. A. Patel", reg_end: "31/12/2026", days: "14" }, fields: ["subject", "heading", "body"] },
  { key: "sms_otp", title: "SMS — login OTP", vars: { code: "428913" }, fields: ["text"] },
  { key: "receipt", title: "Receipt PDF — note & terms", vars: {}, fields: ["note_en", "note_gu", "terms_en", "terms_gu"] },
];

export const TemplatesSection = () => {
  const [t, setT] = useState(null);
  const [sel, setSel] = useState("email_otp");
  const [preview, setPreview] = useState(null);
  const [versions, setVersions] = useState([]);
  const [showV, setShowV] = useState(false);
  useEffect(() => { adminApi.getTemplates().then(setT); }, []);
  if (!t) return <p className="text-sm text-gmoaa-text-muted">Loading…</p>;

  const def = TPL_DEFS.find((d) => d.key === sel);
  const cur = t[sel] || {};
  const setField = (k, v) => setT((x) => ({ ...x, [sel]: { ...x[sel], [k]: v } }));
  const save = async () => { try { await adminApi.putTemplates(t); toast.success("Templates saved"); } catch { toast.error("Save failed"); } };
  const doPreview = async () => {
    try { const p = await adminApi.previewTemplate({ key: sel, template: cur, variables: def.vars }); setPreview(p); }
    catch { toast.error("Preview failed"); }
  };
  const loadV = async () => { setVersions(await adminApi.templateVersions()); setShowV(true); };
  const restore = async (vid) => { const d = await adminApi.restoreTemplates(vid); setT(d); toast.success("Restored"); setShowV(false); };

  const varHint = Object.keys(def.vars).length ? `Placeholders: ${Object.keys(def.vars).map((k) => `{{${k}}}`).join(", ")}` : "No placeholders";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl font-semibold text-gmoaa-green">Templates</h2>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={loadV} data-testid="tpl-versions"><History size={14} className="mr-1" /> History</Button>
          <Button onClick={save} data-testid="tpl-save" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save templates</Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {TPL_DEFS.map((d) => (
          <button key={d.key} onClick={() => { setSel(d.key); setPreview(null); setShowV(false); }} data-testid={`tpl-tab-${d.key}`}
            className={`rounded-full px-3 py-1.5 text-xs font-medium ${sel === d.key ? "bg-gmoaa-green text-white" : "bg-white text-gmoaa-text-dark border border-gmoaa-border-subtle"}`}>{d.title}</button>
        ))}
      </div>
      {showV ? (
        <div className="space-y-2 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
          <p className="text-sm font-medium">Previous template versions</p>
          {versions.length === 0 && <p className="text-xs text-gmoaa-text-muted">No earlier versions yet.</p>}
          {versions.map((v) => (
            <div key={v.id} className="flex items-center justify-between rounded border border-gmoaa-border-subtle p-2 text-xs">
              <span>{new Date(v.at).toLocaleString()} · {v.actor}</span>
              <Button size="sm" variant="outline" onClick={() => restore(v.id)}>Restore</Button>
            </div>
          ))}
          <Button size="sm" variant="ghost" onClick={() => setShowV(false)}>Close</Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
          <div className="space-y-3 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
            <p className="text-xs text-gmoaa-text-muted">{varHint}</p>
            {def.fields.map((fld) => (
              <Field key={fld} label={fld.replace("_", " ")} textarea={fld === "body" || fld.startsWith("terms") || fld.startsWith("note")}
                value={cur[fld]} onChange={(v) => setField(fld, v)} testid={`tpl-${sel}-${fld}`} />
            ))}
            {sel !== "receipt" && <Button size="sm" variant="outline" onClick={doPreview} data-testid="tpl-preview-btn"><Eye size={14} className="mr-1" /> Preview</Button>}
          </div>
          <div className="rounded-xl border border-gmoaa-border-subtle bg-white p-5" data-testid="tpl-preview">
            <p className="mb-2 text-xs font-semibold text-gmoaa-text-muted">Preview</p>
            {preview ? (
              preview.text ? <p className="text-sm">{preview.text}</p> : (
                <div>
                  <p className="mb-2 text-sm font-medium">Subject: {preview.subject}</p>
                  <div className="rounded border border-gmoaa-border-subtle p-3" dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preview.html) }} />
                </div>
              )
            ) : <p className="text-sm text-gmoaa-text-muted">Click Preview to render with sample values.</p>}
          </div>
        </div>
      )}
    </div>
  );
};

/* ------------------------- ADMIN USERS ------------------------- */
export const AdminUsersSection = () => {
  const [users, setUsers] = useState([]);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "content_editor", password: "" });
  const [resetFor, setResetFor] = useState(null);
  const [newPw, setNewPw] = useState("");

  const load = () => adminApi.listAdminUsers().then(setUsers);
  useEffect(() => { load(); }, []);

  const create = async () => {
    try {
      await adminApi.createAdminUser(form);
      toast.success("Account created");
      setCreating(false); setForm({ name: "", email: "", role: "content_editor", password: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create account"); }
  };
  const changeRole = async (u, role) => {
    try { await adminApi.changeAdminRole(u.id, role); toast.success("Role updated"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not change role"); }
  };
  const doReset = async () => {
    try { await adminApi.resetAdminPassword(resetFor.id, newPw); toast.success("Password reset"); setResetFor(null); setNewPw(""); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not reset password"); }
  };
  const del = async (u) => {
    if (!window.confirm(`Remove ${u.email}? This cannot be undone.`)) return;
    try { await adminApi.deleteAdminUser(u.id); toast.success("Account removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not remove account"); }
  };

  return (
    <div className="max-w-2xl space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-2xl font-semibold text-gmoaa-green">Admin users &amp; roles</h2>
        <Button onClick={() => setCreating((c) => !c)} data-testid="adminuser-new" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover"><Plus size={16} className="mr-1" /> Add user</Button>
      </div>

      {creating && (
        <div className="space-y-3 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="Name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testid="adminuser-name" />
            <Field label="Email" value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} testid="adminuser-email" />
          </div>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <p className="mb-1 text-xs font-medium text-gmoaa-text-muted">Role</p>
              <select className="h-9 w-full rounded border border-gmoaa-border-subtle bg-white text-sm" value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))} data-testid="adminuser-role">
                <option value="content_editor">Content editor (content only)</option>
                <option value="admin">Super admin (everything)</option>
              </select>
            </div>
            <Field label="Initial password (min 8 chars)" type="password" value={form.password} onChange={(v) => setForm((f) => ({ ...f, password: v }))} testid="adminuser-password" />
          </div>
          <Button onClick={create} data-testid="adminuser-create" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Create account</Button>
        </div>
      )}

      <div className="rounded-xl border border-gmoaa-border-subtle bg-white p-5">
        {users.map((u) => (
          <div key={u.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-gmoaa-border-subtle py-3 last:border-0" data-testid={`adminuser-${u.email}`}>
            <div><p className="text-sm font-medium">{u.name || u.email}</p><p className="text-xs text-gmoaa-text-muted">{u.email}</p></div>
            <div className="flex items-center gap-2">
              <select className="h-8 rounded border border-gmoaa-border-subtle bg-white text-xs" value={u.role} onChange={(e) => changeRole(u, e.target.value)} data-testid={`adminuser-rolesel-${u.email}`}>
                <option value="content_editor">Content editor</option>
                <option value="admin">Super admin</option>
              </select>
              <Button size="sm" variant="outline" onClick={() => { setResetFor(u); setNewPw(""); }} data-testid={`adminuser-reset-${u.email}`}><KeyRound size={14} /></Button>
              <Button size="sm" variant="ghost" className="text-gmoaa-red" onClick={() => del(u)} data-testid={`adminuser-del-${u.email}`}><Trash2 size={14} /></Button>
            </div>
          </div>
        ))}
      </div>

      {resetFor && (
        <div className="space-y-3 rounded-xl border border-gmoaa-border-subtle bg-white p-5">
          <p className="text-sm font-medium">Reset password for {resetFor.email}</p>
          <Field label="New password (min 8 chars)" type="password" value={newPw} onChange={setNewPw} testid="adminuser-newpw" />
          <div className="flex gap-2">
            <Button onClick={doReset} data-testid="adminuser-reset-save" className="bg-gmoaa-green text-white hover:bg-gmoaa-green-hover">Save new password</Button>
            <Button variant="ghost" onClick={() => setResetFor(null)}>Cancel</Button>
          </div>
        </div>
      )}

      <p className="text-xs text-gmoaa-text-muted">A <b>Content editor</b> can edit Pages, Text &amp; translations and Media only — never money, roster, settings or templates. Super admin controls everything. The last super admin cannot be removed or demoted.</p>
    </div>
  );
};
